import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { debounce } from "throttle-debounce";
import PagingRequest from "../../../models/PagingRequest";
import SchoolAuditLog from "../../../models/schoolAuditLog";
import { IRootState } from "../../../store";
import DispatchProps from "../../../utils/DispatchProps";
import ApiService from "../../ApiService";
import Actions from "../../../redux/Actions";
import { MessageBarType, TextField } from "office-ui-fabric-react";
import Table, { TableColumn } from "../../Table";
import TableCell from "../../TableCell";
import TablePaging from "../../TablePaging";
import { TableRowProps } from "../../TableRow";

interface AuditLogsProps {
    apiService: ApiService
    pagingRequest: PagingRequest;
    pagingRequestChanged: (pagingRequest: PagingRequest) => void;
    hideFilter?: boolean;
}

interface AuditLogsState {
    auditLogs: SchoolAuditLog[];
    totalAuditLogs: number;
    loading: boolean;
}

class AuditLogs extends Component<AuditLogsProps & DispatchProps, AuditLogsState>
{
    private readonly debouncedLoad: debounce<() => void>;

    constructor(props: AuditLogsProps & DispatchProps) {
        super(props);

        this.debouncedLoad = debounce(200, this.load);

        this.state = {
            auditLogs: [],
            totalAuditLogs: 0,
            loading: false,
        }
    }

    async componentDidMount() {
        this.debouncedLoad();
    }

    async componentDidUpdate(prevProps: Readonly<AuditLogsProps & DispatchProps>, prevState: any): Promise<void> {
        if (!PagingRequest.AreEqual(prevProps.pagingRequest, this.props.pagingRequest)) {
            this.debouncedLoad();
        }
    }

    private async load(): Promise<void> {
        this.setState({ loading: true });
        try {
            var auditLogs = await this.props.apiService.devGetAuditLogs(this.props.pagingRequest);

            this.setState({
                auditLogs: auditLogs.results,
                totalAuditLogs: auditLogs.total,
                loading: false
            });
        }
        catch (e) {
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Unable to load audit logs`, MessageBarType.error));
            this.setState({ loading: false });
        }
    }

    private sortCallback = (propertyName: string) => (desc: boolean): void => {
        var newPageRequest = PagingRequest.Copy(this.props.pagingRequest);
        newPageRequest.sortField = propertyName
        newPageRequest.sortDesc = desc;
        newPageRequest.page = 0;
        this.props.pagingRequestChanged(newPageRequest);
    }

    private next = (): (() => void) | undefined => {

        if (this.state.loading) {
            return undefined;
        }

        var newPageRequest = PagingRequest.Copy(this.props.pagingRequest);
        newPageRequest.addPage(1);

        if (newPageRequest.page! >= newPageRequest.GetNumberOfPages(this.state.totalAuditLogs)) {
            return undefined;
        }

        return () => this.props.pagingRequestChanged(newPageRequest);
    }

    private previous = (): (() => void) | undefined => {

        if (this.state.loading) {
            return undefined;
        }

        var newPageRequest = PagingRequest.Copy(this.props.pagingRequest);

        if (newPageRequest.page! <= 0) {
            return undefined;
        }

        newPageRequest.addPage(-1);

        return () => this.props.pagingRequestChanged(newPageRequest);
    }

    private filter = (event: any, newValue?: string) => {
        var filterRequest = PagingRequest.Copy(this.props.pagingRequest);
        filterRequest.filter = newValue;
        filterRequest.page = 0;
        this.props.pagingRequestChanged(filterRequest);
    }

    private columns: TableColumn[] = [
        {
            title: "School",
            propertyPath: "school",
            flex: 2,
            sortCallback: this.sortCallback("schoolCode")
        },
        {
            title: "User",
            propertyPath: "userOid",
            flex: 2,
        },
        {
            title: "TimeStamp",
            propertyPath: "timestamp",
            flex: 2,
            sortCallback: this.sortCallback("timestamp")
        },
        {
            title: "Type",
            propertyPath: "name",
            flex: 2,
        },
        {
            title: "Detail",
            propertyPath: "detail",
            flex: 4,
        },
    ];

    private rowProps = (auditLog: SchoolAuditLog): TableRowProps => {
        return {
            enableRowLink: false
        };
    }

    private buildRow = (auditLog: SchoolAuditLog, column: TableColumn, index: number): JSX.Element | undefined => {
        switch (column.propertyPath) {

            case "school":
                return <TableCell key={`${index}-${column.propertyPath}`} flex={column.flex}>
                    <span>{auditLog.schoolCode}</span>
                    <span className={"cdb-table__extra-detail"}>{auditLog.tenantId}</span>
                </TableCell>;

            case "detail":
                return <TableCell key={`${index}-${column.propertyPath}`} flex={column.flex}>
                    <span className={"cdb-table__extra-detail cdb-table__extra-detail--ellipsis"} title={auditLog.detail}>
                        {auditLog.detail}
                    </span>
                </TableCell>;

            default:
                return undefined;
        }
    }

    private buildFilter() {
        if (this.props.hideFilter !== true) {
            return <div className="filter-controls p-top-1rem">
                <div className="filter-controls__row">
                    <TextField className="filter-controls__input"
                        autoFocus
                        label="School Code / UserOid / Type"
                        placeholder="Filter"
                        value={this.props.pagingRequest.filter}
                        onChange={this.filter} />
                </div>
            </div>
        }
    }

    public render() {
        return (
            <Fragment>
                {this.buildFilter()}

                <Table title="Audit Logs"
                    items={this.state.auditLogs}
                    columns={this.columns}
                    cellForColumn={this.buildRow}
                    rowPropsForItem={this.rowProps}
                    loading={this.state.loading}
                    enableSort={true}
                    sortAsc={!this.props.pagingRequest.sortDesc}>
                </Table>

                <TablePaging total={this.props.pagingRequest.GetNumberOfPages(this.state.totalAuditLogs)}
                    count={this.props.pagingRequest.page! + 1}
                    next={this.next()}
                    previous={this.previous()}>
                </TablePaging>

            </Fragment>
        );
    }
}


export default connect(
    (state: IRootState, ownProps: { pagingRequest: PagingRequest, pagingRequestChanged: (pagingRequest: PagingRequest) => void, hideFilter?: boolean }): AuditLogsProps => {
        return {
            apiService: state.auth.apiService,
            pagingRequest: ownProps.pagingRequest,
            pagingRequestChanged: ownProps.pagingRequestChanged,
            hideFilter: ownProps.hideFilter
        };
    }
)(AuditLogs);